import {
  AUTOMATIC_STREAK_QUEUE_ITEM_STATUS,
  AUTOMATIC_STREAK_QUEUE_LEAVE_REASON,
  type AutomaticStreakQueueLeaveReason,
} from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useHubUser } from "@/hooks/user/useHubUser";
import { getUserAutomaticStreakQueueItemV2 } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

export interface UseUserStreakQueueItemOptions {
  campaignName: string;
  enabled?: boolean;
}

const KICK_REASONS = new Set<AutomaticStreakQueueLeaveReason>([
  AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.INSUFFICIENT_BALANCE,
  AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.EXCHANGE_ACCOUNT_DISCONNECTED,
  AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.MISSING_SOCIAL_ACCOUNTS,
  AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.INVALID_CONFIGURATION,
]);

/**
 * Single source of truth for the user's queue row on a campaign. Replaces the
 * previous split between `useUserStreakQueueState` and
 * `useUserStreakQueueLastKickReason` — one round-trip returns everything the
 * UI needs to branch on (`isInQueue`, `lastKickReason`, …).
 */
export const useUserStreakQueueItem = ({ campaignName, enabled = true }: UseUserStreakQueueItemOptions) => {
  const { auth, hubUser } = useHubUser();

  const query = useQuery({
    queryKey: [QueryKeys.AUTOMATIC_STREAK, "queue", "item", hubUser?.defiAddress, campaignName],
    queryFn: async () => {
      const result = await getUserAutomaticStreakQueueItemV2(auth, campaignName);

      if (isServerApiRfcError(result)) {
        throw new Error(result.detail);
      }
      if (isServerApiError(result)) {
        throw new Error(result.message);
      }

      return result;
    },
    enabled: enabled && !!campaignName && !!auth,
    staleTime: 30_000,
  });

  const status = query.data?.status ?? null;
  const leaveReason = query.data?.leaveReason ?? null;
  const wasKicked =
    status === AUTOMATIC_STREAK_QUEUE_ITEM_STATUS.OUT && leaveReason !== null && KICK_REASONS.has(leaveReason);

  return {
    isInQueue: status === AUTOMATIC_STREAK_QUEUE_ITEM_STATUS.IN,
    joinedAt: query.data?.joinedAt ?? null,
    /** The kick reason when the user was last evicted by the system; null for voluntary leaves and never-joined. */
    lastKickReason: wasKicked ? leaveReason : null,
    kickedAt: wasKicked ? (query.data?.leftAt ?? null) : null,
    /** Remaining ticket-bought prioritized epochs on the row (0 when unprioritized). */
    priorityCounter: query.data?.priorityCounter ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error?.message,
  };
};
